import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { ErrorView } from "@/components/ui/layout/error/errorView"
import { Text } from "@/components/ui/baseUi/text"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { ExerciseDetail } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/components/exerciseDetail"
import { TemplateWeekView } from "@/modules/template/components/sidebarView/templateWeekView"
import type { TemplateElementEntity } from "@/modules/template/entities/templateElementEntity"
import { useTemplate, useTemplateElement } from "@/modules/template/hooks/useTemplate"
import { TemplateType } from "@/modules/template/types/templateType"
import { useImperativeHandle, useState } from "react"

export type TemplateModalViewRef = {
  open: () => void
}

type TemplateModalViewProps = {
  templateId: string
  className?: string
  ref: React.Ref<TemplateModalViewRef>
} & Omit<React.ComponentProps<"div">, "ref">

export function TemplateModalView({ templateId, ref, className, ...props }: TemplateModalViewProps) {
  const [showModal, setShowModal] = useState(false)
  const { data: templateElement } = useTemplateElement(templateId)
  const { data: template } = useTemplate(templateId)

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  const width = template.templateType === TemplateType.WEEK ? "80%" : "512px"

  return (
    <Dialog
      open={showModal}
      onOpenChange={(open) => {
        if (!open) setShowModal(false)
      }}
    >
      <DialogContent className={cn(`max-h-[80vh] w-full max-w-[${width}] overflow-y-auto`, className)} {...props}>
        <DialogHeader>
          <DialogTitle>
            <Text fontStyle="TM">{word("global.previewOf", { elementName: template.name })}</Text>
          </DialogTitle>
        </DialogHeader>
        <TemplateModalContent templateElement={templateElement} templateId={templateId} />
      </DialogContent>
    </Dialog>
  )
}

type TemplateModalContentProps = {
  templateElement: TemplateElementEntity
  templateId: string
}

function TemplateModalContent({ templateElement, templateId }: TemplateModalContentProps) {
  if (templateElement.type === TemplateType.EXERCISE) {
    return <ExerciseDetail exercise={templateElement.exercise} parentSession={null} isCoach={false} />
  } else if (templateElement.type === TemplateType.WEEK) {
    return <TemplateWeekView templateId={templateId} />
  } else {
    return <ErrorView />
  }
}
